// ─────────────────────────────────────────────────────────────────────────
// Shared domain types. Imported by the React app AND the Netlify functions
// so the wire contract stays in one place.
// ─────────────────────────────────────────────────────────────────────────

/** A single role as extracted from a company's official career portal. */
export interface JobOpening {
  title: string;
  department: string;
  location: string;
  /** Direct link to the official posting (never an aggregator URL). */
  officialUrl: string;
  description: string;
  /** Seniority tier inferred for this role, e.g. "entry", "mid", "senior". */
  seniority?: string;
}

/**
 * One raw listing as seen on a Naukri (or similar aggregator) results page.
 * Naukri exposes a company NAME but not the company's official posting URL —
 * that's the whole reason the import stage exists: harvest company names here,
 * then resolve each company's official portal via career-discovery.
 */
export interface NaukriListing {
  company: string;
  title: string;
  location?: string;
}

/** Request body for the naukri-search function. */
export interface NaukriSearchRequest {
  /** Role focus to search Naukri for, e.g. "AI Engineer". */
  roleQuery: string;
  /** Optional city filter, e.g. "Bengaluru". */
  location?: string;
}

/** Response from the naukri-search function. */
export interface NaukriSearchResponse {
  listings: NaukriListing[];
  /** The Naukri search URL the results came from (for transparency). */
  sourceUrl: string;
  /** Human-readable note on how the search went (for the activity feed). */
  note: string;
}

/** Request body for the career-discovery function. */
export interface DiscoveryRequest {
  companyName: string;
  roleQuery: string;
  /**
   * Optional resume text. When present, discovery filters openings to those
   * matching the candidate's experience level.
   */
  resumeText?: string;
}

/** Response from the career-discovery function. */
export interface DiscoveryResponse {
  companyName: string;
  /** The official corporate career portal the agent discovered. */
  careerPortalUrl: string;
  openings: JobOpening[];
  /** Short, human-readable note on how discovery went (for the activity feed). */
  note: string;
}

/** One suggested edit to a resume bullet, proposed by the agent. */
export interface BulletDiff {
  /** Existing line this replaces, if any (absent means it's a new bullet). */
  original?: string;
  suggested: string;
  reason: string;
}

/** Request body for the match-resume function. */
export interface MatchRequest {
  jobTitle: string;
  company: string;
  jobDescription: string;
  resumeText: string;
}

/** Response from the match-resume function. */
export interface MatchResponse {
  /** Fit score, 0–100. */
  score: number;
  summary: string;
  /** Skills present in the job that the resume already covers. */
  strengths: string[];
  /** Skills the job wants that the resume is missing. */
  gaps: string[];
  bulletDiffs: BulletDiff[];
}

// ── Client-only view model ────────────────────────────────────────────────

export type PipelineStage = "discovered" | "interested" | "applied";

export const STAGE_ORDER: PipelineStage[] = [
  "discovered",
  "interested",
  "applied",
];

export const STAGE_LABEL: Record<PipelineStage, string> = {
  discovered: "Discovered",
  interested: "Shortlisted",
  applied: "Applied",
};

/** A role placed on the Kanban board, with local UI state attached. */
export interface JobCardModel extends JobOpening {
  id: string;
  company: string;
  stage: PipelineStage;
  addedAt: number;
  match?: MatchResponse;
}

/** An entry in the live agent-activity feed. */
export interface ActivityEntry {
  id: string;
  at: number;
  /** Which surface triggered this — a WebMCP tool call or a manual UI action. */
  source: "agent" | "user" | "system";
  tool?: string;
  message: string;
  status: "running" | "ok" | "error";
}
